<template>
  <div class="inspection-banner" :class="bannerClass">
    <div class="inspection-banner__icon">
      <img :src="searchIconSrc" alt="" class="inspection-banner__icon-img" />
    </div>

    <div class="inspection-banner__content">
      <InspectionLevelBadge
        v-if="state === 'accepted'"
        class="inspection-banner__readiness"
        :level="normalizedReadinessLevel"
      />

      <div class="inspection-banner__copy">
        <template v-if="state === 'pending'">
          <span class="inspection-banner__copy-lead">
            You requested an inspection for this responsibility at
            {{ readinessLevelLabel }} level from
          </span>
          <span class="inspection-banner__copy-sub">
            The inspector was notified of your request and will be expected to accept or decline it.
          </span>
        </template>

        <template v-else-if="state === 'accepted'">
          <span class="inspection-banner__copy-lead"> Your inspection request was accepted </span>
          <span class="inspection-banner__copy-sub">
            Please contact the Inspector to schedule an inspection.
          </span>
        </template>

        <template v-else-if="state === 'make_request'">
          <span class="inspection-banner__copy-lead">
            Request an inspection when you are ready to be inspected
          </span>
        </template>

        <template v-else-if="state === 'no_inspectors'">
          <span class="inspection-banner__copy-lead">
            <span class="inspection-banner__copy-alert" aria-hidden="true">
              <img :src="alertIconSrc" alt="" class="inspection-banner__copy-alert-icon" />
            </span>
            No coach is currently available for this Responsibility
          </span>
          <span class="inspection-banner__copy-sub"> Please contact your administrator. </span>
        </template>

        <template v-else-if="state === 'declined'">
          <span class="inspection-banner__copy-lead">
            <span class="inspection-banner__copy-alert" aria-hidden="true">
              <img :src="alertIconSrc" alt="" class="inspection-banner__copy-alert-icon" />
            </span>
            Your inspection request was declined by the inspector
          </span>
          <span class="inspection-banner__copy-sub">{{ formattedReason }}</span>
        </template>

        <template v-else-if="state === 'auto_declined'">
          <span class="inspection-banner__copy-lead">
            <span class="inspection-banner__copy-alert" aria-hidden="true">
              <img :src="alertIconSrc" alt="" class="inspection-banner__copy-alert-icon" />
            </span>
            Your inspection request was automatically declined
          </span>
          <span class="inspection-banner__copy-sub">{{ formattedReason }}</span>
        </template>

        <template v-else-if="state === 'failed'">
          <span class="inspection-banner__copy-lead">
            <span class="inspection-banner__copy-alert" aria-hidden="true">
              <img :src="alertIconSrc" alt="" class="inspection-banner__copy-alert-icon" />
            </span>
            Your inspection failed
          </span>
          <span class="inspection-banner__copy-sub">{{ formattedReason }}</span>
        </template>

        <template v-else-if="state === 'failed_coach_unavailable'">
          <span class="inspection-banner__copy-lead">
            <span class="inspection-banner__copy-alert" aria-hidden="true">
              <img :src="alertIconSrc" alt="" class="inspection-banner__copy-alert-icon" />
            </span>
            Your inspection failed
          </span>
          <span class="inspection-banner__copy-sub">
            {{ formattedReason }}. Your inspector is no longer a coach.
          </span>
        </template>
      </div>

      <div v-if="showEmployeeCard" class="inspection-banner__employee">
        <ExAvatar
          class="inspection-banner__employee-avatar"
          :picture="inspectorAvatar"
          :firstName="inspectorFirstName"
          :lastName="inspectorLastName"
          size="medium"
        />
        <div class="inspection-banner__employee-meta">
          <span class="inspection-banner__employee-name">{{ inspectorName }}</span>
          <span v-if="inspectorTitle" class="inspection-banner__employee-title">
            {{ inspectorTitle }}
          </span>
        </div>
      </div>

      <span v-if="showExpedited" class="inspection-banner__expedited"> Expedited </span>
    </div>

    <div class="inspection-banner__actions">
      <button
        v-if="showRequest"
        class="inspection-banner__request-btn"
        type="button"
        @click="$emit('onRequest')"
      >
        Request Inspection
      </button>
      <button
        v-if="showRetry"
        class="inspection-banner__retry-btn"
        type="button"
        @click="$emit('onRetry')"
      >
        Retry
      </button>
      <button
        v-if="showCancel"
        class="inspection-banner__cancel-btn"
        type="button"
        @click="$emit('onCancel')"
      >
        Cancel
      </button>
      <ChatterButton
        v-if="showChatter"
        class="inspection-banner__chatter"
        aria-label="Chat with inspector"
        @onClick="$emit('onChatter')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import InspectionLevelBadge from "@components/InspectionLevelBadge/InspectionLevelBadge.vue";
import ChatterButton from "@components/ChatterButton/ChatterButton.vue";

const searchIconSrc = require("@components/assets/search-refraction.svg");
const alertIconSrc = require("@components/assets/alert-circle-error.svg");

const props = defineProps({
  /**
   * Explicit banner variant. Each state carries its own fixed copy and layout.
   * Pending / Accepted are the only states that honour `expedited`.
   */
  state: {
    type: String,
    required: true,
    validator(value) {
      return [
        "pending",
        "accepted",
        "make_request",
        "no_inspectors",
        "declined",
        "auto_declined",
        "failed",
        "failed_coach_unavailable",
      ].includes(value);
    },
  },
  /** Only applies to `pending` and `accepted`. */
  expedited: {
    type: Boolean,
    default: false,
  },
  /** The single accepted/requested inspector shown on the Employee Card. */
  inspector: {
    type: Object,
    default: () => null,
  },
  /** Readiness level shown on the Accepted badge and Pending copy. */
  readinessLevel: {
    type: String,
    default: "Professional",
  },
  /** The outcome reason for Declined / Auto Declined / Failed. */
  reason: {
    type: String,
    default: "",
  },
});

defineEmits(["onRequest", "onRetry", "onCancel", "onChatter"]);

const VALID_READINESS_LEVELS = ["Apprentice", "Professional", "Coach", "Master"];

const normalizedReadinessLevel = computed(() => {
  const raw = String(props.readinessLevel || "").trim();
  const titleCased = raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : "";
  return VALID_READINESS_LEVELS.includes(titleCased) ? titleCased : "Professional";
});

const readinessLevelLabel = computed(() => normalizedReadinessLevel.value.toLowerCase());

const formattedReason = computed(() => {
  const reason = String(props.reason || "").trim();
  return reason ? `Reason: ${reason}` : "Reason:";
});

const showEmployeeCard = computed(() =>
  ["pending", "accepted", "declined", "auto_declined", "failed"].includes(props.state)
);

const showExpedited = computed(
  () => props.expedited && ["pending", "accepted"].includes(props.state)
);

const showCancel = computed(() =>
  [
    "pending",
    "accepted",
    "declined",
    "auto_declined",
    "failed",
    "failed_coach_unavailable",
  ].includes(props.state)
);

const showRetry = computed(() => props.state === "failed");

const showRequest = computed(() =>
  ["make_request", "failed_coach_unavailable"].includes(props.state)
);

const showChatter = computed(() =>
  [
    "pending",
    "accepted",
    "declined",
    "auto_declined",
    "failed",
    "failed_coach_unavailable",
  ].includes(props.state)
);

const bannerClass = computed(() => ({
  "inspection-banner--declined": [
    "declined",
    "auto_declined",
    "failed",
    "failed_coach_unavailable",
  ].includes(props.state),
}));

function splitFullName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

const inspectorAvatar = computed(() => {
  const i = props.inspector;
  if (!i) return "";
  return i.img || i.picture || i.avatar || i.avatarUrl || i.photoUrl || "";
});

const inspectorName = computed(() => {
  const i = props.inspector;
  if (!i) return "";
  if (i.name || i.fullName) return i.name || i.fullName;
  const first = i.firstName || "";
  const last = i.lastName || "";
  return [first, last].filter(Boolean).join(" ").trim();
});

const inspectorTitle = computed(() => {
  const i = props.inspector;
  if (!i) return "";
  return i.title || i.position || "";
});

const inspectorFirstName = computed(() => {
  const i = props.inspector;
  if (!i) return "";
  if (i.firstName) return i.firstName;
  return splitFullName(inspectorName.value).first;
});

const inspectorLastName = computed(() => {
  const i = props.inspector;
  if (!i) return "";
  if (i.lastName) return i.lastName;
  return splitFullName(inspectorName.value).last;
});
</script>

<style scoped>
.inspection-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.inspection-banner--declined {
  background: #fef3f2;
}

.inspection-banner__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
}

.inspection-banner__icon-img {
  width: 48px;
  height: 48px;
  display: block;
}

.inspection-banner__content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  flex: 1;
  min-width: 0;
}

.inspection-banner__readiness {
  flex-shrink: 0;
}

.inspection-banner__copy {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.inspection-banner__copy-lead {
  color: #344054;
  font-weight: 400;
}

.inspection-banner__copy-sub {
  color: #475467;
  font-weight: 400;
}

.inspection-banner__copy-alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  vertical-align: middle;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(217, 45, 32, 0.3), 0 0 0 4px rgba(217, 45, 32, 0.1);
}

.inspection-banner__copy-alert-icon {
  display: block;
  width: 20px;
  height: 20px;
}

.inspection-banner__employee {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.inspection-banner__employee-avatar {
  flex-shrink: 0;
}

.inspection-banner__employee-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.inspection-banner__employee-name {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.inspection-banner__employee-title {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
}

.inspection-banner__expedited {
  color: #c11574;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  padding: 2px 10px;
  border-radius: 9999px;
  border: 1px solid #fcccee;
  background: #fdf2fa;
  flex-shrink: 0;
  white-space: nowrap;
}

.inspection-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.inspection-banner__request-btn,
.inspection-banner__retry-btn {
  display: flex;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #7f56d9;
  background: #7f56d9;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.inspection-banner__cancel-btn {
  display: flex;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #d92d20;
  background: #ffffff;
  color: #d92d20;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.inspection-banner__chatter {
  flex-shrink: 0;
}
</style>
